import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_MS = 30000; // 30s

/**
 * Activity tracker for invite-link guests.
 * - Page-view event on every route change
 * - Heartbeat every 30s while the tab is visible (so total_time_seconds is real "active" time)
 * - Throttled per render so a no-op for non-invite users is free
 *
 * Mount once at the app root inside the auth context.
 */
export function useInviteTracking() {
  const { user } = useAuth();
  const location = useLocation();
  const lastBeat = useRef(0);
  const isInvite = user && user.role === 'invite_lead';

  // Track page views
  useEffect(() => {
    if (!isInvite) return;
    const path = location.pathname + (location.search || '');
    axios.post(`${API}/invites/track`, {
      event_type: 'page_view',
      event_data: { path },
    }).catch(() => { /* silent */ });
  }, [isInvite, location.pathname, location.search]);

  // Heartbeat
  useEffect(() => {
    if (!isInvite) return;

    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      // Avoid bursting: only beat if at least 25s have passed since last
      if (now - lastBeat.current < 25000) return;
      lastBeat.current = now;
      axios.post(`${API}/invites/heartbeat`, { seconds: 30 }).catch(() => { /* silent */ });
    };

    const id = setInterval(beat, HEARTBEAT_MS);
    // Beat once on mount so we don't lose the first 30s
    beat();
    // Beat again when tab regains focus
    const onVis = () => beat();
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isInvite]);
}

/**
 * Helper for components to fire a specific event when a user takes a meaningful action.
 * Safe to call from anywhere; no-op for non-invite users (backend short-circuits).
 */
export function trackInviteEvent(eventType, eventData = {}) {
  try {
    axios.post(`${API}/invites/track`, {
      event_type: eventType,
      event_data: eventData,
    }).catch(() => { /* silent */ });
  } catch { /* noop */ }
}
